import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Ensure standard datepicker styles are loaded
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/*
============================================================
CROP COEFFICIENT DATA
============================================================
*/

const kcData = {
  wheat: {
    "11": "0.70",
    "12": "0.95",
    "01": "1.13",
    "02": "1.13",
    "03": "0.92",
    "04": "0.49",
  },

  cotton: {
    "05": "0.35",
    "06": "0.61",
    "07": "1.05",
    "08": "1.15",
    "09": "1.11",
    "10": "0.84",
    "11": "0.63",
  },

  maize: {
    "05": "0.31",
    "06": "0.75",
    "07": "1.12",
    "08": "1.06",
    "09": "0.57",
  },

  sugarcane: {
    "01": "0.94",
    "02": "0.79",
    "03": "0.40",
    "04": "0.62",
    "05": "1.02",
    "06": "1.21",
    "07": "1.21",
    "08": "1.21",
    "09": "1.21",
    "10": "1.21",
    "11": "1.20",
    "12": "1.10",
  },

  rice: {
    "06": "1.15",
    "07": "1.15",
    "08": "1.30",
    "09": "1.30",
    "10": "1.20",
    "11": "1.10",
  },

  barley: {
    "11": "0.30",
    "12": "0.80",
    "01": "0.90",
    "02": "1.15",
    "03": "1.00",
  },

  beans: {
    "06": "0.40",
    "07": "0.70",
    "08": "1.06",
    "09": "0.95",
  },

  canola: {
    "10": "0.35",
    "11": "0.70",
    "12": "0.90",
    "01": "1.15",
    "02": "1.10",
    "03": "1.00",
  },

  castor: {
    "03": "0.35",
    "04": "0.70",
    "05": "0.80",
    "06": "1.00",
    "07": "1.15",
    "08": "1.10",
    "09": "1.00",
    "10": "1.00",
  },

  grams: {
    "10": "0.40",
    "11": "0.65",
    "12": "0.90",
    "01": "0.90",
    "02": "1.00",
    "03": "0.95",
  },

  groundnut: {
    "03": "0.40",
    "04": "0.50",
    "05": "0.80",
    "06": "1.10",
    "07": "0.85",
    "08": "0.85",
  },

  millet: {
    "06": "0.40",
    "07": "0.70",
    "08": "1.00",
    "09": "0.90",
    "10": "0.90",
  },

  rapeseed: {
    "10": "0.35",
    "11": "0.70",
    "12": "1.00",
    "01": "1.20",
    "02": "1.20",
    "03": "1.00",
    "04": "1.00",
  },

  safflower: {
    "11": "0.50",
    "12": "0.70",
    "01": "0.80",
    "02": "1.00",
    "03": "1.00",
    "04": "0.80",
  },

  sesame: {
    "06": "0.35",
    "07": "0.80",
    "08": "0.90",
    "09": "1.10",
    "10": "1.00",
    "11": "1.00",
  },

  sorghum: {
    "07": "0.40",
    "08": "0.70",
    "09": "1.00",
    "10": "1.10",
    "11": "0.80",
    "12": "0.81",
  },

  soybeans: {
    "02": "0.40",
    "03": "0.80",
    "04": "1.00",
    "05": "1.15",
    "06": "0.80",
    "07": "0.40",
    "08": "0.80",
    "09": "1.00",
    "10": "1.15",
    "11": "0.80",
  },

  sugarbeats: {
    "10": "0.35",
    "11": "0.70",
    "12": "0.90",
    "01": "1.20",
    "02": "1.20",
    "03": "1.10",
    "04": "0.80",
  },

  sunflower: {
    "02": "0.40",
    "03": "0.70",
    "04": "1.00",
    "05": "1.20",
    "06": "0.80",
  },

  sweetcorn: {
    "02": "0.30",
    "03": "0.70",
    "04": "1.15",
    "05": "1.10",
    "06": "1.00",
    "07": "0.30",
    "08": "0.70",
    "09": "1.15",
    "10": "1.10",
    "11": "1.00",
  },

  toria: {
    "10": "0.35",
    "11": "0.60",
    "12": "0.70",
    "01": "1.10",
    "02": "1.15",
    "03": "1.00",
    "04": "0.95",
  },

  tobacco: {
    "10": "0.30",
    "11": "0.70",
    "12": "0.80",
    "01": "1.10",
    "02": "1.20",
    "03": "0.90",
    "04": "0.90",
  },
};

/*
============================================================
CROP LIST
============================================================
*/

const crops = [
  { key: "wheat", name: "Wheat" },
  { key: "rice", name: "Rice" },
  { key: "sugarcane", name: "Sugarcane" },
  { key: "cotton", name: "Cotton" },
  { key: "maize", name: "Maize" },
  { key: "barley", name: "Barley" },
  { key: "beans", name: "Beans" },
  { key: "canola", name: "Canola" },
  { key: "castor", name: "Castor Beans" },
  { key: "grams", name: "Grams" },
  { key: "groundnut", name: "Groundnut" },
  { key: "millet", name: "Millet" },
  { key: "rapeseed", name: "Rapeseed" },
  { key: "safflower", name: "Safflower" },
  { key: "sesame", name: "Sesame" },
  { key: "sorghum", name: "Sorghum" },
  { key: "soybeans", name: "Soybeans" },
  { key: "sugarbeats", name: "Sugar Beets" },
  { key: "sunflower", name: "Sunflower" },
  { key: "sweetcorn", name: "Sweet Corn" },
  { key: "toria", name: "Toria" },
  { key: "tobacco", name: "Tobacco" },
];

/*
============================================================
FALLBACK WEATHER / ETo DATA
============================================================
*/

const defaultEto = [4.5, 5.0, 4.8];

const defaultWeather = [
  {
    Tmean: 22.5,
    humidity: 45,
    sunshineHours: 9.2,
    windSpeed: 2.1,
    pressure: 1013,
  },
  {
    Tmean: 24.0,
    humidity: 40,
    sunshineHours: 9.8,
    windSpeed: 2.5,
    pressure: 1011,
  },
  {
    Tmean: 23.1,
    humidity: 42,
    sunshineHours: 9.0,
    windSpeed: 1.9,
    pressure: 1012,
  },
];

/*
============================================================
HELPERS
============================================================
*/

const toLocalDateString = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "-";

  const parts = dateString.split("-");

  if (parts.length !== 3) {
    return dateString;
  }

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const generateDateRange = (startDate, endDate) => {
  const dates = [];

  if (!startDate || !endDate) {
    return dates;
  }

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start > end
  ) {
    return dates;
  }

  const current = new Date(start);

  while (current <= end) {
    dates.push(toLocalDateString(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/*
============================================================
MAIN COMPONENT
============================================================
*/

const CropWaterRequirement = () => {
  const today = new Date();

  const todayString = toLocalDateString(today);

  const [selectedCrops, setSelectedCrops] = useState([]);

  const [area, setArea] = useState("1");

  /*
  ONE RANGE PICKER
  */
  const [dateRange, setDateRange] = useState([today, today]);

  const [startDate, endDate] = dateRange;

  const startDateString = startDate
    ? toLocalDateString(startDate)
    : "";

  const endDateString = endDate
    ? toLocalDateString(endDate)
    : "";

  const [rows, setRows] = useState([]);

  const [showCropModal, setShowCropModal] = useState(false);

  const [error, setError] = useState("");

  const [summary, setSummary] = useState({
    cubicMeters: 0,
    inches: 0,
    cubicFeet: 0,
    timeHours: 0,
  });

  /*
  ==========================================================
  DATE RANGE
  ==========================================================
  */

  const dates = useMemo(() => {
    return generateDateRange(
      startDateString,
      endDateString
    );
  }, [startDateString, endDateString]);

  /*
  ==========================================================
  CROP SELECTION
  ==========================================================
  */

  const toggleCrop = (cropKey) => {
    setSelectedCrops((previous) => {
      if (previous.includes(cropKey)) {
        return previous.filter(
          (item) => item !== cropKey
        );
      }

      return [...previous, cropKey];
    });
  };

  const getSelectedCropNames = () => {
    return crops
      .filter((crop) =>
        selectedCrops.includes(crop.key)
      )
      .map((crop) => crop.name)
      .join(", ");
  };

  /*
  ==========================================================
  CALCULATION ENGINE
  ==========================================================
  */

  const calculateCWR = () => {
    try {
      setError("");

      if (!startDateString || !endDateString) {
        setRows([]);
        setSummary({
          cubicMeters: 0,
          inches: 0,
          cubicFeet: 0,
          timeHours: 0,
        });

        setError("Please select a date range.");
        return;
      }

      const numericArea = parseFloat(area);

      if (
        !Number.isFinite(numericArea) ||
        numericArea <= 0
      ) {
        setRows([]);
        setSummary({
          cubicMeters: 0,
          inches: 0,
          cubicFeet: 0,
          timeHours: 0,
        });

        setError(
          "Please enter a valid cultivated area greater than 0."
        );
        return;
      }

      if (selectedCrops.length === 0) {
        setRows([]);
        setSummary({
          cubicMeters: 0,
          inches: 0,
          cubicFeet: 0,
          timeHours: 0,
        });

        setError("Please select at least one crop.");
        return;
      }

      if (startDateString > endDateString) {
        setRows([]);
        setSummary({
          cubicMeters: 0,
          inches: 0,
          cubicFeet: 0,
          timeHours: 0,
        });

        setError(
          "Start date cannot be greater than end date."
        );
        return;
      }

      if (dates.length === 0) {
        setRows([]);
        setSummary({
          cubicMeters: 0,
          inches: 0,
          cubicFeet: 0,
          timeHours: 0,
        });

        setError(
          "No valid dates found for the selected range."
        );
        return;
      }

      let cubicMetersSum = 0;
      let inchesSum = 0;

      const calculatedRows = [];

      dates.forEach((dateStr, i) => {
        const etoIndex = i % defaultEto.length;
        const weatherIndex =
          i % defaultWeather.length;

        const eto = parseFloat(
          defaultEto[etoIndex]
        );

        const weather =
          defaultWeather[weatherIndex];

        if (!Number.isFinite(eto) || !weather) {
          return;
        }

        const parts = dateStr.split("-");

        if (parts.length !== 3) {
          return;
        }

        const month = parts[1];

        let dailyCWRM3 = 0;
        let dailyETCInches = 0;

        selectedCrops.forEach((crop) => {
          try {
            if (
              kcData[crop] &&
              kcData[crop][month] !== undefined &&
              kcData[crop][month] !== null
            ) {
              const kc = parseFloat(
                kcData[crop][month]
              );

              if (!Number.isFinite(kc)) {
                return;
              }

              dailyCWRM3 +=
                ((eto * kc) / 1000) *
                numericArea *
                4047;

              dailyETCInches +=
                eto * kc * 0.0394;
            }
          } catch (cropError) {
            console.error(
              `Error calculating crop ${crop}:`,
              cropError
            );
          }
        });

        cubicMetersSum += dailyCWRM3;
        inchesSum += dailyETCInches;

        calculatedRows.push({
          date: formatDateForDisplay(dateStr),
          eto,
          cwr: dailyCWRM3,
          etcInches: dailyETCInches,
          temperature: weather.Tmean,
          humidity: weather.humidity,
          sunshine: weather.sunshineHours,
          wind: weather.windSpeed,
          pressure: weather.pressure,
        });
      });

      const cubicFeet =
        cubicMetersSum * 35.315;

      const secondsPerAcre =
        cubicFeet / 0.8;

      const timeReqHours =
        (secondsPerAcre / 3600) * 0.6;

      setRows(calculatedRows);

      setSummary({
        cubicMeters: cubicMetersSum,
        inches: inchesSum,
        cubicFeet,
        timeHours: timeReqHours,
      });
    } catch (calculationError) {
      console.error(
        "Crop Water Requirement calculation error:",
        calculationError
      );

      setRows([]);

      setSummary({
        cubicMeters: 0,
        inches: 0,
        cubicFeet: 0,
        timeHours: 0,
      });

      setError(
        "Something went wrong while calculating crop water requirement."
      );
    }
  };

  /*
  ==========================================================
  AUTO CALCULATE
  ==========================================================
  */

  useEffect(() => {
    if (
      selectedCrops.length > 0 &&
      startDateString &&
      endDateString &&
      area
    ) {
      calculateCWR();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCrops,
    startDateString,
    endDateString,
    area,
  ]);

  /*
  ==========================================================
  FORMAT NUMBERS
  ==========================================================
  */

  const formatNumber = (
    value,
    decimals = 2
  ) => {
    if (
      value === null ||
      value === undefined ||
      !Number.isFinite(Number(value))
    ) {
      return "-";
    }

    return Number(value).toFixed(decimals);
  };

  /*
  ==========================================================
  RESET
  ==========================================================
  */

  const handleReset = () => {
    setSelectedCrops([]);
    setArea("1");
    setDateRange([today, today]);
    setRows([]);
    setError("");

    setSummary({
      cubicMeters: 0,
      inches: 0,
      cubicFeet: 0,
      timeHours: 0,
    });
  };

  /*
  ==========================================================
  BACK BUTTON
  ==========================================================
  */

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      console.warn(
        "No previous browser history available."
      );
    }
  };

  /*
  ==========================================================
  EXPORT DATA PREPARATION
  ==========================================================
  */

  const getExportRows = () => {
    return rows.map((row) => ({
      Date: row.date,
      "ETo (mm)": Number(row.eto || 0).toFixed(2),
      "CWR (m³)": Number(row.cwr || 0).toFixed(2),
      "ETc (Inches)": Number(
        row.etcInches || 0
      ).toFixed(2),
      "Temperature (°C)": Number(
        row.temperature || 0
      ).toFixed(1),
      "Humidity (%)": row.humidity ?? "-",
      "Sunshine (hrs)": Number(
        row.sunshine || 0
      ).toFixed(1),
      "Wind (m/s)": row.wind ?? "-",
      "Pressure (hPa)": row.pressure ?? "-",
    }));
  };

  /*
  ==========================================================
  EXPORT EXCEL
  ==========================================================
  */

  const exportExcel = () => {
    try {
      if (rows.length === 0) {
        setError(
          "Please calculate the water requirement before exporting."
        );
        return;
      }

      const workbook =
        XLSX.utils.book_new();

      /*
      SUMMARY SHEET
      */

      const summaryData = [
        ["Crop Water Requirement Report"],
        [],
        ["Selected Crops", getSelectedCropNames()],
        [
          "Start Date",
          startDateString
            ? formatDateForDisplay(
                startDateString
              )
            : "-",
        ],
        [
          "End Date",
          endDateString
            ? formatDateForDisplay(
                endDateString
              )
            : "-",
        ],
        ["Cultivated Area (Acres)", area],
        [],
        ["Summary", "Value"],
        [
          "Total Water Needed (m³)",
          formatNumber(
            summary.cubicMeters
          ),
        ],
        [
          "Total Depth Needed (Inches)",
          formatNumber(summary.inches),
        ],
        [
          "Total Volume Needed (ft³)",
          formatNumber(
            summary.cubicFeet
          ),
        ],
        [
          "Estimated Irrigation Duration (Hours)",
          formatNumber(
            summary.timeHours
          ),
        ],
      ];

      const summarySheet =
        XLSX.utils.aoa_to_sheet(
          summaryData
        );

      /*
      DAILY DATA SHEET
      */

      const dataSheet =
        XLSX.utils.json_to_sheet(
          getExportRows()
        );

      XLSX.utils.book_append_sheet(
        workbook,
        summarySheet,
        "Summary"
      );

      XLSX.utils.book_append_sheet(
        workbook,
        dataSheet,
        "Daily Data"
      );

      const fileName =
        `Crop_Water_Requirement_${startDateString || todayString}_${endDateString || todayString}.xlsx`;

      XLSX.writeFile(
        workbook,
        fileName
      );
    } catch (exportError) {
      console.error(
        "Excel export error:",
        exportError
      );

      setError(
        "Unable to export Excel file."
      );
    }
  };

  /*
  ==========================================================
  EXPORT CSV
  ==========================================================
  */

  const exportCSV = () => {
    try {
      if (rows.length === 0) {
        setError(
          "Please calculate the water requirement before exporting."
        );
        return;
      }

      const exportRows = getExportRows();

      const headers =
        Object.keys(exportRows[0]);

      const csvRows = [
        headers.join(","),
        ...exportRows.map((row) =>
          headers
            .map((header) => {
              const value =
                row[header] ?? "";

              const stringValue =
                String(value);

              if (
                stringValue.includes(",") ||
                stringValue.includes('"')
              ) {
                return `"${stringValue.replace(
                  /"/g,
                  '""'
                )}"`;
              }

              return stringValue;
            })
            .join(",")
        ),
      ];

      const blob = new Blob(
        [csvRows.join("\n")],
        {
          type: "text/csv;charset=utf-8;",
        }
      );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        `Crop_Water_Requirement_${startDateString || todayString}_${endDateString || todayString}.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error(
        "CSV export error:",
        exportError
      );

      setError(
        "Unable to export CSV file."
      );
    }
  };

  /*
  ==========================================================
  EXPORT PDF
  ==========================================================
  */

  const exportPDF = () => {
    try {
      if (rows.length === 0) {
        setError(
          "Please calculate the water requirement before exporting."
        );
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(18);

      doc.text(
        "Crop Water Requirement Report",
        14,
        15
      );

      doc.setFontSize(10);

      doc.text(
        `Crops: ${getSelectedCropNames()}`,
        14,
        23
      );

      doc.text(
        `Date Range: ${formatDateForDisplay(
          startDateString
        )} - ${formatDateForDisplay(
          endDateString
        )}`,
        14,
        29
      );

      doc.text(
        `Cultivated Area: ${area} Acres`,
        14,
        35
      );

      /*
      DAILY TABLE
      */

      autoTable(doc, {
        startY: 42,

        head: [
          [
            "Date",
            "ETo (mm)",
            "CWR (m³)",
            "ETc (In)",
            "Temp (°C)",
            "Humidity (%)",
            "Sunshine",
            "Wind (m/s)",
            "Pressure",
          ],
        ],

        body: rows.map((row) => [
          row.date,
          formatNumber(row.eto),
          formatNumber(row.cwr),
          formatNumber(row.etcInches),
          formatNumber(
            row.temperature,
            1
          ),
          row.humidity ?? "-",
          formatNumber(
            row.sunshine,
            1
          ),
          row.wind ?? "-",
          row.pressure ?? "-",
        ]),

        styles: {
          fontSize: 7,
          cellPadding: 2,
        },

        headStyles: {
          fontSize: 7,
        },

        margin: {
          left: 10,
          right: 10,
        },
      });

      /*
      SUMMARY
      */

      const finalY =
        doc.lastAutoTable?.finalY
          ? doc.lastAutoTable.finalY + 10
          : 50;

      doc.setFontSize(11);

      doc.text(
        "Summary",
        14,
        finalY
      );

      autoTable(doc, {
        startY: finalY + 4,

        head: [
          ["Metric", "Value"],
        ],

        body: [
          [
            "Total Water Needed",
            `${formatNumber(
              summary.cubicMeters
            )} m³`,
          ],
          [
            "Total Depth Needed",
            `${formatNumber(
              summary.inches
            )} Inches`,
          ],
          [
            "Total Volume Needed",
            `${formatNumber(
              summary.cubicFeet
            )} ft³`,
          ],
          [
            "Estimated Irrigation Duration",
            `${formatNumber(
              summary.timeHours
            )} Hours`,
          ],
        ],

        styles: {
          fontSize: 9,
        },

        margin: {
          left: 14,
        },
      });

      doc.save(
        `Crop_Water_Requirement_${startDateString || todayString}_${endDateString || todayString}.pdf`
      );
    } catch (exportError) {
      console.error(
        "PDF export error:",
        exportError
      );

      setError(
        "Unable to export PDF file."
      );
    }
  };

  /*
  ==========================================================
  UI
  ==========================================================
  */

  return (
    <div className="cwr-page">

      <div className="cwr-container">

        {/* ==================================================
            TOP NAVIGATION
        ================================================== */}

        <div className="top-navigation">

          <button
            type="button"
            className="back-button"
            onClick={handleBack}
          >
            ← Back
          </button>

        </div>

        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="cwr-header">

          <h2>
            Crop Water Requirement
          </h2>

          <p>
            Calculate crop water requirement
            based on crop, cultivated area and
            selected date range.
          </p>

        </div>

        {/* ==================================================
            INPUT CARD
        ================================================== */}

        <div className="cwr-card">

          {/* DATE RANGE INLINE CALENDAR */}

          <div className="input-group">

            <label>
              Select Date Range (Start & End Date)
            </label>

            <div className="inline-calendar-container">
              <DatePicker
                selectsRange
                inline
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                  setError("");
                }}
                dateFormat="dd-MM-yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>

          </div>

          {/* RANGE INFO */}

          {startDate && endDate && (
            <div className="date-info">

              <strong>
                Selected Period:
              </strong>{" "}

              {formatDateForDisplay(
                startDateString
              )}

              {" → "}

              {formatDateForDisplay(
                endDateString
              )}

              {" "}

              ({dates.length} day
              {dates.length !== 1
                ? "s"
                : ""})

            </div>
          )}

          {/* CROP SELECTOR */}

          <div className="input-group">

            <label>
              Select Crop(s)
            </label>

            <button
              type="button"
              className="select-button"
              onClick={() =>
                setShowCropModal(true)
              }
            >

              <span
                className={
                  selectedCrops.length > 0
                    ? "select-text"
                    : "placeholder"
                }
              >
                {selectedCrops.length > 0
                  ? getSelectedCropNames()
                  : "Select Crop(s)"}
              </span>

              <span className="arrow">
                ▼
              </span>

            </button>

          </div>

          {/* AREA */}

          <div className="input-group">

            <label htmlFor="area">
              Cultivated Area (Acres)
            </label>

            <input
              id="area"
              type="number"
              min="0.01"
              step="0.01"
              value={area}
              onChange={(e) => {
                setArea(e.target.value);
                setError("");
              }}
              placeholder="Acres"
            />

          </div>

          {/* ERROR */}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* BUTTONS */}

          <div className="button-row">

            <button
              type="button"
              className="calculate-button"
              onClick={calculateCWR}
            >
              Calculate CWR
            </button>

            <button
              type="button"
              className="reset-button"
              onClick={handleReset}
            >
              Reset
            </button>

          </div>

        </div>

        {/* ==================================================
            SELECTED CROPS
        ================================================== */}

        {selectedCrops.length > 0 && (
          <div className="selected-container">

            <div className="selected-label">
              Selected Crops:
            </div>

            <div className="selected-value">
              {getSelectedCropNames()}
            </div>

          </div>
        )}

        {/* ==================================================
            TABLE
        ================================================== */}

        {rows.length > 0 && (
          <div className="table-wrapper">

            {/* EXPORT BUTTONS */}

            <div className="export-toolbar">

              <div className="export-title">
                Calculation Results
              </div>

              <div className="export-buttons">

                <button
                  type="button"
                  className="export-button excel"
                  onClick={exportExcel}
                >
                  ↓ Excel
                </button>

                <button
                  type="button"
                  className="export-button csv"
                  onClick={exportCSV}
                >
                  ↓ CSV
                </button>

                <button
                  type="button"
                  className="export-button pdf"
                  onClick={exportPDF}
                >
                  ↓ PDF
                </button>

              </div>

            </div>

            <div className="table-scroll">

              <table className="cwr-table">

                <thead>

                  <tr>

                    <th>
                      Date
                    </th>

                    <th>
                      ETo
                      <br />
                      (mm)
                    </th>

                    <th>
                      CWR
                      <br />
                      (m³)
                    </th>

                    <th>
                      ETc
                      <br />
                      (Inches)
                    </th>

                    <th>
                      Temp
                      <br />
                      (°C)
                    </th>

                    <th>
                      Humidity
                      <br />
                      (%)
                    </th>

                    <th>
                      Sunshine
                      <br />
                      (hrs)
                    </th>

                    <th>
                      Wind
                      <br />
                      (m/s)
                    </th>

                    <th>
                      Pressure
                      <br />
                      (hPa)
                    </th>

                  </tr>

                </thead>

                <tbody>

                  {rows.map(
                    (row, index) => (

                      <tr
                        key={`${row.date}-${index}`}
                      >

                        <td>
                          {row.date}
                        </td>

                        <td>
                          {formatNumber(
                            row.eto
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            row.cwr
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            row.etcInches
                          )}
                        </td>

                        <td>
                          {row.temperature !==
                          undefined
                            ? `${formatNumber(
                                row.temperature,
                                1
                              )} °C`
                            : "-"}
                        </td>

                        <td>
                          {row.humidity !==
                          undefined
                            ? `${row.humidity} %`
                            : "-"}
                        </td>

                        <td>
                          {row.sunshine !==
                          undefined
                            ? formatNumber(
                                row.sunshine,
                                1
                              )
                            : "-"}
                        </td>

                        <td>
                          {row.wind !==
                          undefined
                            ? `${row.wind} m/s`
                            : "-"}
                        </td>

                        <td>
                          {row.pressure !==
                          undefined
                            ? `${row.pressure} hPa`
                            : "-"}
                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

            {/* ==================================================
                SUMMARY
            ================================================== */}

            <div className="summary-container">

              <div className="summary-row">

                <span>
                  Total Water Needed
                </span>

                <strong>
                  {formatNumber(
                    summary.cubicMeters
                  )}{" "}
                  m³
                </strong>

              </div>

              <div className="summary-row">

                <span>
                  Total Depth Needed
                </span>

                <strong>
                  {formatNumber(
                    summary.inches
                  )}{" "}
                  Inches
                </strong>

              </div>

              <div className="summary-row">

                <span>
                  Total Volume Needed
                </span>

                <strong>
                  {formatNumber(
                    summary.cubicFeet
                  )}{" "}
                  ft³
                </strong>

              </div>

              <div className="summary-row">

                <span>
                  Estimated Irrigation Duration
                </span>

                <strong>
                  {formatNumber(
                    summary.timeHours
                  )}{" "}
                  Hours
                </strong>

              </div>

            </div>

          </div>
        )}

        {/* ==================================================
            EMPTY STATE
        ================================================== */}

        {rows.length === 0 &&
          selectedCrops.length === 0 && (

            <div className="empty-state">

              Select crop(s), date range and
              cultivated area to calculate crop
              water requirement.

            </div>

          )}

      </div>

      {/* ==================================================
          CROP MODAL
      ================================================== */}

      {showCropModal && (

        <div
          className="modal-overlay"
          onClick={() =>
            setShowCropModal(false)
          }
        >

          <div
            className="crop-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="modal-header">

              <h3>
                Select Crop(s)
              </h3>

              <button
                type="button"
                className="close-button"
                onClick={() =>
                  setShowCropModal(false)
                }
              >
                ×
              </button>

            </div>

            <div className="crop-list">

              {crops.map((crop) => {

                const selected =
                  selectedCrops.includes(
                    crop.key
                  );

                return (

                  <label
                    key={crop.key}
                    className={
                      selected
                        ? "crop-option selected"
                        : "crop-option"
                    }
                  >

                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        toggleCrop(
                          crop.key
                        )
                      }
                    />

                    <span>
                      {crop.name}
                    </span>

                  </label>

                );
              })}

            </div>

            <div className="modal-footer">

              <button
                type="button"
                className="done-button"
                onClick={() =>
                  setShowCropModal(false)
                }
              >
                Done
              </button>

            </div>

          </div>

        </div>

      )}

      {/* ==================================================
          STYLES
      ================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        .cwr-page {
          width: 100%;
          min-height: 100vh;
          background: #f8f9fa;
          padding: 24px;
          color: #222;
        }

        .cwr-container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* TOP NAVIGATION */

        .top-navigation {
          margin-bottom: 16px;
        }

        .back-button {
          height: 40px;
          padding: 0 16px;
          border: 1px solid #d5d5d5;
          border-radius: 7px;
          background: #fff;
          color: #333;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: 0.2s;
        }

        .back-button:hover {
          background: #f0f0f0;
          border-color: #bbb;
        }

        /* HEADER */

        .cwr-header {
          margin-bottom: 20px;
        }

        .cwr-header h2 {
          margin: 0 0 5px;
          font-size: 28px;
          font-weight: 700;
        }

        .cwr-header p {
          margin: 0;
          color: #777;
          font-size: 15px;
        }

        /* CARD */

        .cwr-card {
          background: #fff;
          border-radius: 10px;
          padding: 20px;
          margin-bottom: 14px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
        }

        /* INPUTS */

        .input-group {
          margin-bottom: 16px;
        }

        .input-group label {
          display: block;
          margin-bottom: 7px;
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .input-group input {
          width: 100%;
          height: 46px;
          border: 1px solid #d5d5d5;
          border-radius: 7px;
          padding: 0 12px;
          font-size: 15px;
          color: #222;
          background: #fff;
          outline: none;
          transition: border-color 0.2s;
        }

        .input-group input:focus {
          border-color: #1976d2;
        }

        /* INLINE CALENDAR STYLING */

        .inline-calendar-container {
          display: flex;
          justify-content: center;
          background: #fafafa;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
        }

        .react-datepicker {
          font-family: inherit;
          border: none;
          background-color: transparent;
        }

        .react-datepicker__header {
          background-color: transparent;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .react-datepicker__current-month, 
        .react-datepicker-time__header, 
        .react-datepicker-year-header {
          font-weight: 700;
          color: #1e293b;
          font-size: 16px;
          margin-bottom: 8px;
        }

        .react-datepicker__day-name {
          color: #64748b;
          font-weight: 600;
          width: 36px;
          line-height: 36px;
        }

        .react-datepicker__day {
          width: 36px;
          line-height: 36px;
          border-radius: 50%;
          color: #334155;
          font-weight: 500;
          margin: 2px;
        }

        .react-datepicker__day:hover {
          background-color: #e2e8f0;
          border-radius: 50%;
        }

        .react-datepicker__day--in-range,
        .react-datepicker__day--in-selecting-range {
          background-color: #e0f2fe !important;
          color: #0284c7 !important;
          border-radius: 0 !important;
        }

        .react-datepicker__day--range-start,
        .react-datepicker__day--selecting-range-start {
          background-color: #1976d2 !important;
          color: #fff !important;
          border-top-left-radius: 50% !important;
          border-bottom-left-radius: 50% !important;
        }

        .react-datepicker__day--range-end {
          background-color: #1976d2 !important;
          color: #fff !important;
          border-top-right-radius: 50% !important;
          border-bottom-right-radius: 50% !important;
        }

        .react-datepicker__day--keyboard-selected {
          background-color: transparent;
        }

        .react-datepicker__month-select, 
        .react-datepicker__year-select {
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 13px;
          color: #334155;
          background-color: #fff;
          outline: none;
          margin: 0 4px;
        }

        .date-info {
          background: #eaf3ff;
          border: 1px solid #d8e9fa;
          color: #1976d2;
          border-radius: 7px;
          padding: 10px 12px;
          margin-bottom: 16px;
          font-size: 13px;
        }

        /* SELECT */

        .select-button {
          width: 100%;
          min-height: 48px;
          border: 1px solid #d5d5d5;
          border-radius: 7px;
          padding: 10px 12px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          text-align: left;
        }

        .select-text {
          color: #222;
          font-size: 15px;
        }

        .placeholder {
          color: #999;
          font-size: 15px;
        }

        .arrow {
          color: #777;
          font-size: 11px;
          margin-left: 10px;
        }

        /* ERROR */

        .error-message {
          background: #fff0f0;
          border: 1px solid #f1b7b7;
          color: #c62828;
          border-radius: 7px;
          padding: 10px 12px;
          margin-bottom: 15px;
          font-size: 14px;
        }

        /* BUTTONS */

        .button-row {
          display: flex;
          gap: 10px;
          margin-top: 4px;
        }

        .calculate-button,
        .reset-button {
          height: 46px;
          border: none;
          border-radius: 7px;
          padding: 0 24px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }

        .calculate-button {
          background: #1976d2;
          color: #fff;
        }

        .calculate-button:hover {
          background: #1565c0;
        }

        .reset-button {
          background: #e9ecef;
          color: #333;
        }

        .reset-button:hover {
          background: #dee2e6;
        }

        /* SELECTED CROPS */

        .selected-container {
          background: #eaf3ff;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 14px;
        }

        .selected-label {
          font-size: 13px;
          font-weight: 700;
          color: #555;
          margin-bottom: 3px;
        }

        .selected-value {
          font-size: 14px;
          color: #1976d2;
        }

        /* TABLE */

        .table-wrapper {
          background: #fff;
          border-radius: 8px;
          border: 1px solid #ddd;
          overflow: hidden;
        }

        .table-scroll {
          width: 100%;
          overflow-x: auto;
        }

        .cwr-table {
          width: 100%;
          min-width: 1050px;
          border-collapse: collapse;
        }

        .cwr-table th {
          min-height: 58px;
          padding: 10px 8px;
          text-align: center;
          vertical-align: middle;
          font-size: 12px;
          font-weight: 700;
          color: #333;
          background: #f1f3f5;
          border-right: 1px solid #ddd;
          border-bottom: 1px solid #ccc;
          white-space: nowrap;
        }

        .cwr-table td {
          min-height: 48px;
          padding: 10px 8px;
          text-align: center;
          vertical-align: middle;
          font-size: 12px;
          color: #333;
          border-right: 1px solid #ddd;
          border-bottom: 1px solid #ddd;
          white-space: nowrap;
        }

        .cwr-table tbody tr:hover {
          background: #f8fbff;
        }

        /* EXPORT TOOLBAR */

        .export-toolbar {
          min-height: 64px;
          padding: 12px 14px;
          border-bottom: 1px solid #ddd;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          background: #fff;
        }

        .export-title {
          font-size: 15px;
          font-weight: 700;
          color: #333;
        }

        .export-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .export-button {
          height: 38px;
          padding: 0 14px;
          border: none;
          border-radius: 6px;
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: 0.2s;
        }

        .export-button:hover {
          opacity: 0.88;
          transform: translateY(-1px);
        }

        .export-button.excel {
          background: #217346;
        }

        .export-button.csv {
          background: #6c757d;
        }

        .export-button.pdf {
          background: #c62828;
        }

        /* SUMMARY */

        .summary-container {
          padding: 12px;
        }

        .summary-row {
          min-height: 48px;
          background: #f0f7ff;
          border: 1px solid #d8e9fa;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
        }

        .summary-row span {
          font-size: 14px;
          font-weight: 700;
          color: #333;
        }

        .summary-row strong {
          font-size: 14px;
          font-weight: 700;
          color: #1976d2;
          text-align: right;
        }

        /* EMPTY STATE */

        .empty-state {
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 30px;
          text-align: center;
          color: #777;
          font-size: 14px;
        }

        /* MODAL */

        .modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0, 0, 0, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .crop-modal {
          width: 100%;
          max-width: 500px;
          max-height: 85vh;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          height: 60px;
          padding: 0 18px;
          border-bottom: 1px solid #eee;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 19px;
          font-weight: 700;
        }

        .close-button {
          border: none;
          background: transparent;
          font-size: 28px;
          color: #555;
          cursor: pointer;
          line-height: 1;
        }

        .crop-list {
          overflow-y: auto;
          padding: 5px 0;
        }

        .crop-option {
          min-height: 50px;
          padding: 0 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          font-size: 15px;
        }

        .crop-option:hover {
          background: #f8fbff;
        }

        .crop-option.selected {
          background: #f0f7ff;
        }

        .crop-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #1976d2;
        }

        .modal-footer {
          padding: 14px 18px;
          border-top: 1px solid #eee;
        }

        .done-button {
          width: 100%;
          height: 46px;
          border: none;
          border-radius: 7px;
          background: #1976d2;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }

        .done-button:hover {
          background: #1565c0;
        }

        /* RESPONSIVE */

        @media (max-width: 768px) {

          .cwr-page {
            padding: 14px;
          }

          .cwr-header h2 {
            font-size: 23px;
          }

          .button-row {
            flex-direction: column;
          }

          .calculate-button,
          .reset-button {
            width: 100%;
          }

          .summary-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
          }

          .summary-row strong {
            text-align: left;
          }

          .export-toolbar {
            align-items: flex-start;
            flex-direction: column;
          }

          .export-buttons {
            width: 100%;
          }

          .export-button {
            flex: 1;
          }

          .modal-overlay {
            padding: 10px;
          }

          .crop-modal {
            max-height: 90vh;
          }

        }
      `}</style>

    </div>
  );
};

export default CropWaterRequirement;